import { createContext, useContext } from "react";

import type { SearchResponse } from "@/features/search/api/queries";
import type { SearchRoute } from "@/features/search/api/route";
import type { SearchNavigation } from "@/features/search/hooks/useSearchNavigation";
import type { UseQueryResult } from "@tanstack/react-query";

export type SearchContextValue = {
  /** What the current URL means, and the params sent for it. */
  route: SearchRoute;
  query: UseQueryResult<SearchResponse>;
  navigation: SearchNavigation;
};

const SearchContext = createContext<SearchContextValue | null>(null);

export const SearchContextProvider = SearchContext.Provider;

/**
 * Access the current search state. Throws if used outside `<SearchProvider>` so
 * mis-wiring fails at render, not with silent `undefined`.
 */
export const useSearchContext = (): SearchContextValue => {
  const value = useContext(SearchContext);

  if (!value) {
    throw new Error("useSearchContext must be used within <SearchProvider>");
  }

  return value;
};

/** Convenience selectors — keep components declarative. */
export const useSearchData = () => useSearchContext().query.data;
export const useSearchNavigationContext = () => useSearchContext().navigation;
